import React, { useState } from 'react';
import { BarChart3, CalendarX, ClipboardList, Activity, LineChart } from 'lucide-react';
import CaseloadDiagnosis from '../Analytics/CaseloadDiagnosis';
import NoShowAnalysis from '../Analytics/NoShowAnalysis';
import TreatmentPlanReview from '../Analytics/TreatmentPlanReview';
import IndividualPHQ9Tracker from '../Analytics/IndividualPHQ9Tracker';
import IndividualGAD7Tracker from '../Analytics/IndividualGAD7Tracker';

/**
 * Per-clinician drill-down: renders the EXACT analytics pages the clinician
 * sees, read-only, with the clinician's own data injected. Reuses the real
 * clinician container components (viewData + readOnly), so the supervisor sees
 * everything the clinician can see — same charts, same clinical analytics — but
 * cannot edit the data. Revenue (income-by-month) stays out per the monitoring
 * model.
 */
const TYPES = [
  { type: 'caseload-diagnosis', label: 'Caseload Diagnosis', icon: BarChart3, Comp: CaseloadDiagnosis },
  { type: 'no-show-analysis', label: 'No-Show Analysis', icon: CalendarX, Comp: NoShowAnalysis },
  { type: 'treatment-plan-review', label: 'Treatment Plan Review', icon: ClipboardList, Comp: TreatmentPlanReview },
  { type: 'individual-phq9-tracker', label: 'PHQ-9 Tracker', icon: Activity, Comp: IndividualPHQ9Tracker },
  { type: 'individual-gad7-tracker', label: 'GAD-7 Tracker', icon: LineChart, Comp: IndividualGAD7Tracker },
];

const ClinicianFullView = ({ clinician }) => {
  const [type, setType] = useState(TYPES[0].type);
  const active = TYPES.find(t => t.type === type);
  const data = (clinician.charts && clinician.charts[type]) || [];
  const Comp = active.Comp;

  return (
    <div>
      {/* Chart-type navigation — mirrors the clinician's own analytics menu */}
      <div className="flex flex-wrap gap-2 mb-5">
        {TYPES.map(t => {
          const has = ((clinician.charts && clinician.charts[t.type]) || []).length > 0;
          const Icon = t.icon;
          const activeTab = type === t.type;
          return (
            <button
              key={t.type}
              onClick={() => setType(t.type)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition ${
                activeTab ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
              {!has && <span className={`text-xs ${activeTab ? 'opacity-80' : 'text-gray-400'}`}>(no data)</span>}
            </button>
          );
        })}
      </div>

      {/* The clinician's actual analytics page for the selected chart type */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
        {data.length === 0 ? (
          <div className="p-16 text-center text-gray-400">
            {clinician.name} hasn't entered any {active.label} data yet.
          </div>
        ) : (
          <Comp
            key={`${clinician.email}|${type}`}
            viewData={data}
            readOnly
            user={{ name: clinician.name }}
          />
        )}
      </div>
    </div>
  );
};

export default ClinicianFullView;
